import { useEffect, useState, type CSSProperties } from 'react';
import { Monitor, RotateCcw } from 'lucide-react';

const DISMISS_KEY = 'skema:mobile-notice-dismissed';

/**
 * Soft gate for small touch screens. Skematiq's editor — the schematic canvas,
 * drag-to-wire, and the button-heavy toolbar — really needs a desktop-sized
 * screen and a pointer, so on a phone we surface a full-screen notice that
 * recommends a computer. It never hard-blocks: a "Continue anyway" escape hatch
 * lets the curious in, and while they're in portrait we nudge them to turn the
 * phone sideways for a little more room. The landing page stays untouched (it's
 * responsive and worth keeping crawlable on mobile) — this only shows once the
 * user is in the editor.
 */
function useSmallScreen() {
  const [state, setState] = useState({ small: false, portrait: false });
  useEffect(() => {
    const check = () => {
      const w = window.innerWidth;
      const h = window.innerHeight;
      const touch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
      // Phone-sized = a touch device whose shorter edge is small. Tablets
      // (~768 short edge) and touch laptops (tall) don't trip this.
      setState({ small: touch && Math.min(w, h) < 680, portrait: h >= w });
    };
    check();
    window.addEventListener('resize', check);
    window.addEventListener('orientationchange', check);
    return () => {
      window.removeEventListener('resize', check);
      window.removeEventListener('orientationchange', check);
    };
  }, []);
  return state;
}

export function MobileNotice() {
  const { small, portrait } = useSmallScreen();
  const [dismissed, setDismissed] = useState(() => {
    try {
      return sessionStorage.getItem(DISMISS_KEY) === '1';
    } catch {
      return false;
    }
  });

  if (!small || dismissed) return null;

  return (
    <div style={overlay}>
      <div style={card}>
        <div style={iconWrap}>
          <Monitor size={30} />
        </div>
        <div style={title}>Skematiq works best on a desktop</div>
        <p style={body}>
          The schematic canvas, drag-to-wire and the toolbar all need a large screen and a
          pointer. On a phone it&apos;s cramped — open Skematiq on a computer for the full
          experience.
        </p>
        {portrait && (
          <div style={rotateHint}>
            <RotateCcw size={16} />
            <span>
              Want to look anyway? <strong>Turn your phone sideways</strong> for a bit more room.
            </span>
          </div>
        )}
        <button
          style={btn}
          onClick={() => {
            try {
              sessionStorage.setItem(DISMISS_KEY, '1');
            } catch {
              // ignore
            }
            setDismissed(true);
          }}
        >
          Continue anyway
        </button>
      </div>
    </div>
  );
}

const overlay: CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'var(--bg, #0d1117)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 24,
  zIndex: 10000,
};
const card: CSSProperties = {
  maxWidth: 380,
  width: '100%',
  textAlign: 'center',
  color: 'var(--text)',
};
const iconWrap: CSSProperties = {
  width: 56,
  height: 56,
  margin: '0 auto 16px',
  borderRadius: 'var(--radius)',
  background: 'var(--accent-soft)',
  color: 'var(--accent)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
};
const title: CSSProperties = { fontSize: 19, fontWeight: 800, marginBottom: 8 };
const body: CSSProperties = {
  fontSize: 14,
  lineHeight: 1.6,
  color: 'var(--text-muted)',
  margin: '0 0 18px',
};
const rotateHint: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 8,
  fontSize: 13,
  lineHeight: 1.5,
  color: 'var(--text)',
  background: 'var(--panel)',
  border: '1px solid var(--panel-border)',
  borderRadius: 'var(--radius)',
  padding: '10px 14px',
  marginBottom: 18,
  textAlign: 'left',
};
const btn: CSSProperties = {
  border: '1px solid var(--panel-border)',
  borderRadius: 'var(--radius-sm)',
  background: 'transparent',
  color: 'var(--text)',
  fontSize: 14,
  fontWeight: 700,
  padding: '10px 20px',
  cursor: 'pointer',
};
